import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("gf_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          setUser(r.data);
          // Fire-and-forget auto-recovery on session boot.
          maybeReconcileSubscription(r.data, token);
        })
        .catch(() => {
          localStorage.removeItem("gf_token");
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password, opts = {}) => {
    const body = { email, password };
    if (opts.remember_me) body.remember_me = true;
    // Send the device-trust token so step-up is skipped on known devices.
    // If we don't have one yet, no header is sent — server will treat this as
    // a new/untrusted device and (usually) issue a challenge.
    const trust = localStorage.getItem("gf_device_trust");
    const headers = trust ? { "X-Device-Trust": trust } : {};
    const r = await axios.post(`${API}/auth/login`, body, { headers });
    // Step-up verification: server issued a challenge instead of a token
    if (r.data && r.data.needs_verification) return { challenge: r.data };
    localStorage.setItem("gf_token", r.data.token);
    if (r.data.device_trust_token) {
      localStorage.setItem("gf_device_trust", r.data.device_trust_token);
    }
    setToken(r.data.token);
    setUser(r.data.user);
    // If a business signs in but local state says no active subscription, ask
    // the backend to check Stripe. Rate-limited to once per 5 min per business.
    maybeReconcileSubscription(r.data.user, r.data.token);
    return r.data.user;
  };

  const verifyLoginChallenge = async ({ challenge_id, code }) => {
    const r = await axios.post(`${API}/auth/login/verify`, { challenge_id, code });
    localStorage.setItem("gf_token", r.data.token);
    if (r.data.device_trust_token) {
      localStorage.setItem("gf_device_trust", r.data.device_trust_token);
    }
    setToken(r.data.token);
    setUser(r.data.user);
    maybeReconcileSubscription(r.data.user, r.data.token);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("gf_token");
    // NOTE (Phase 2): we intentionally KEEP the device-trust token on logout so
    // signing back in on the same browser doesn't retrigger step-up verification.
    // Users who want to fully forget this device can revoke it from Account →
    // Trusted devices, or clear their browser data.
    try { localStorage.removeItem("bf.remember_me"); } catch { /* noop */ }
    setToken(null);
    setUser(null);
  };

  const signup = async (payload) => {
    const r = await axios.post(`${API}/auth/signup`, payload);
    localStorage.setItem("gf_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  /**
   * Exchange an Emergent Google-auth session_id for a BookFreely JWT.
   * Called by <AuthCallback /> right after Google redirects the user back
   * to /auth/callback#session_id=... . Never called from other routes.
   */
  const googleSessionLogin = async ({ session_id, intended_role }) => {
    const r = await axios.post(`${API}/auth/google/session`, { session_id, intended_role });
    localStorage.setItem("gf_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    maybeReconcileSubscription(r.data.user, r.data.token);
    return r.data;
  };

  const refreshUser = async () => {
    if (!token) return;
    const r = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser(r.data);
    return r.data;
  };

  // Auto-recovery: when a business signs in / refreshes and the local
  // subscription looks inactive, ask the backend to reconcile against Stripe.
  // Client-side rate limit (5 min per business) keeps this cheap. The backend
  // enforces its own 60s floor so any misbehaving client can't hammer Stripe.
  const RECONCILE_MIN_MS = 5 * 60 * 1000;
  const maybeReconcileSubscription = async (u, tk) => {
    try {
      if (!u || u.role !== "business" || !tk) return;
      if (u.subscription_active) return; // healthy, nothing to do
      const key = `bf.reconcile.${u.id}`;
      const last = Number(localStorage.getItem(key) || 0);
      if (Date.now() - last < RECONCILE_MIN_MS) return;
      localStorage.setItem(key, String(Date.now()));
      const r = await axios.post(
        `${API}/subscription/reconcile`,
        {},
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      if (r?.data?.changed) {
        // Reconcile applied fields — pull the fresh user doc.
        const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tk}` } });
        setUser(me.data);
      }
    } catch (_) {
      // Reconcile is best-effort — silent failure is fine, the periodic
      // background job on the server will pick it up.
    }
  };

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  // Publicly re-issue the auth token (e.g. after a role change like the
  // customer → business upgrade). Persists to localStorage so it survives
  // reloads and triggers the `useEffect` above which refetches /auth/me.
  const applyToken = (newToken) => {
    if (!newToken) return;
    localStorage.setItem("gf_token", newToken);
    setToken(newToken);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, verifyLoginChallenge, signup, googleSessionLogin, logout, refreshUser, applyToken, authHeader, loading, reconcileSubscription: () => maybeReconcileSubscription(user, token) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);